import { useMemo, useState } from 'react'

import { IconBolt, IconTableOptions } from '@tabler/icons-react'
import { Checkbox, Skeleton, Switch } from 'antd'
import styled from 'styled-components'

import { Button } from '../shared-components/BUI/Button'
import { Modal } from '../shared-components/BUI/Modal'
import { Popover, PopoverItem } from '../shared-components/BUI/Popover'
import { TableFolderBreadcrumbs } from '../shared-components/TableFolderBreadcrumbs'

import { useFilters } from '@/context/filterContext'
import {
  useCheckableListView,
  useListViewInfo,
} from '@/context/listViewContext'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { ListViewColumnConfig } from '@/hooks/useColumnConfig'
import { useRoutePagination } from '@/hooks/useRoutePagination'
import { pluralise, pluraliseWord } from '@/utils/index'

import { PaginationAggregation } from 'types/api.graphql'

const ListHeader = styled.div`
  min-height: 60px;
  padding-left: 1.125rem;
  padding-right: 1.125rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${({ theme }) => theme.colour.neutral.white};
  border-bottom: 1px solid ${({ theme }) => theme.colour.neutral.border};
`

const ListHeaderSection = styled.div`
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
  display: flex;
  align-items: center;
`

const RightListHeaderSection = styled(ListHeaderSection)`
  flex-wrap: wrap;
  justify-content: flex-end;
`

const SelectedCount = styled.span`
  ${({ theme }) => theme.font.label02}
  font-weight: 600;
  color: ${({ theme }) => theme.colour.secondary._900};
`

const ResultCount = styled.div`
  ${({ theme }) => theme.font.label02}
  color: ${({ theme }) => theme.colour.secondary._500};
`

const ActionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const getResultsText = (
  resourceSingular: string,
  aggregation: PaginationAggregation
) => {
  if (!aggregation) return <>&nbsp;</> // space/height in DOM remains

  const { offset, limit, totalCount, totalPages } = aggregation

  if (totalCount === 0) {
    return pluralise(0, resourceSingular)
  }

  if (totalPages <= 1) {
    return pluralise(totalCount, resourceSingular)
  }

  const start = totalCount <= 1 ? totalCount : offset + 1
  const end = Math.min(offset + limit, totalCount)

  return [
    `${start} - ${end}`,
    'of',
    aggregation.totalCount,
    pluraliseWord(aggregation.totalCount, resourceSingular),
  ].join(' ')
}

type MultiModalProps = {
  open: boolean
  closeModal: () => void
  selectedCount: number
  multiActions: React.ReactNode[]
  resourceSingular: string
}

const MultiActionModal: React.FC<MultiModalProps> = ({
  open,
  closeModal,
  selectedCount,
  multiActions,
  resourceSingular,
}) => {
  return (
    <Modal
      open={open}
      onCancel={closeModal}
      title={`Actions for ${pluralise(selectedCount, resourceSingular)}`}
    >
      <ActionsWrapper>{multiActions}</ActionsWrapper>
    </Modal>
  )
}

export type MultiSelectActions = (
  ids: string[],
  options: { reset: () => void; clearSelected: () => void }
) => React.ReactNode[]

type Props = {
  loading?: boolean
  selectAllByDefault?: boolean
  showResultCount?: boolean
  multiSelectActions?: MultiSelectActions
  isPublic?: boolean
  columnConfig?: ListViewColumnConfig
  hideBreadcrumbs?: boolean
}

export const CorePageListViewHeader: React.FC<Props> = ({
  loading,
  showResultCount,
  multiSelectActions,
  isPublic = false,
  columnConfig,
  hideBreadcrumbs = false,
}) => {
  const [multiActionModalOpen, toggleMultiActionModal] = useState(false)
  const { checkable, selectedIds, selectAll, clearAll, selectAllFunction } =
    useCheckableListView()
  const { aggregation, resourceSingular } = useListViewInfo()
  const { clearFilters } = useFilters()
  const isSmall = useBreakpoint('md')
  const pagination = useRoutePagination(20)

  const multiActions = useMemo(() => {
    if (!selectedIds.length) return null

    const reset = () => {
      clearFilters()
      clearAll()
    }

    return multiSelectActions?.(selectedIds, { reset, clearSelected: clearAll })
  }, [selectedIds, multiSelectActions, clearFilters, clearAll])

  if (loading) {
    return (
      <ListHeader>
        <ListHeaderSection>
          <Skeleton.Input size="small" active />
        </ListHeaderSection>
      </ListHeader>
    )
  }

  const totalCount = aggregation?.totalCount || 0
  const selectedCount = selectedIds.length || 0
  const allChecked = !!totalCount && totalCount === selectedCount
  const allOnPageChecked = !!selectedCount && selectedCount === pagination.limit
  const someChecked = !!selectedCount && totalCount !== selectedCount

  const onCheck = () => {
    selectedCount > 0 ? clearAll() : selectAll()
  }

  return (
    <ListHeader>
      <ListHeaderSection>
        {checkable && (
          <Checkbox
            checked={allChecked}
            indeterminate={someChecked}
            onChange={onCheck}
          >
            <SelectedCount>{selectedCount}&nbsp;selected</SelectedCount>
            {allOnPageChecked &&
              selectedCount < totalCount &&
              selectAllFunction && (
                <>
                  {' '}
                  of {totalCount}.{' '}
                  <Button variant="link" onClick={() => selectAll(true)}>
                    Select all?
                  </Button>
                </>
              )}
          </Checkbox>
        )}

        {!isPublic && !hideBreadcrumbs && <TableFolderBreadcrumbs />}
      </ListHeaderSection>

      <RightListHeaderSection>
        {columnConfig?.displayConfig?.length && (
          <Popover
            content={columnConfig.displayConfig.map((config) => {
              return (
                <PopoverItem key={config.value}>
                  <Switch
                    size="small"
                    defaultChecked={config.show}
                    onChange={(c) =>
                      columnConfig.setColumnConfig(config.value, { show: c })
                    }
                  />
                  {config.label}
                </PopoverItem>
              )
            })}
          >
            <Button
              size="small"
              shape="circle"
              variant="secondary-text"
              icon={<IconTableOptions size={16} />}
              style={{ marginRight: '0.5rem' }}
            />
          </Popover>
        )}

        {showResultCount && !selectedIds.length && (
          <ResultCount>
            {getResultsText(resourceSingular, aggregation)}
          </ResultCount>
        )}

        {isSmall ? (
          multiActions?.length > 1 ? (
            <>
              <Button
                size="small"
                variant="primary-text"
                onClick={() => toggleMultiActionModal(true)}
                icon={<IconBolt size={16} />}
              >
                Bulk actions ({selectedCount})
              </Button>
              <MultiActionModal
                open={multiActionModalOpen}
                closeModal={() => toggleMultiActionModal(false)}
                selectedCount={selectedCount}
                multiActions={multiActions}
                resourceSingular={resourceSingular}
              />
            </>
          ) : (
            multiActions
          )
        ) : (
          multiActions
        )}
      </RightListHeaderSection>
    </ListHeader>
  )
}
